// Hand-rolled TypeScript mirrors of the JSON shapes the REST facade returns.
//
// The backend is the source of truth. Every field name below was read off
// the handler that produces it (roastmcp/handlers.py and the `to_dict()` /
// `model_dump()` it calls) — do not add a field here by guessing at it.
// Types stay loose on fields the UI doesn't render (index signatures) so a
// new backend field never breaks the build.

// ---------------------------------------------------------------------------
// List rows — `list_roasts`, `search_roasts`, `list_unrated_roasts`,
// `get_coffee_history` all return `SELECT *` off the `roasts` index table.
// ---------------------------------------------------------------------------

export interface RoastSummary {
  roast_id: string;
  name: string | null;
  roast_date: string | null;
  coffee_id: string | null;
  coffee_label: string | null;
  bean_uid?: string | null;
  // JSON-encoded string array. Use `rowTags()` in rows.ts to read it.
  tags_json: string | null;
  stars: number | null;
  total_roast_time_s: number | null;
  development_pct: number | null;
  max_ror: number | null;
  fc_time_s: number | null;
  development_duration_s?: number | null;
  weight_loss_pct?: number | null;
  drop_temp: number | null;
  drop_ibts_c?: number | null;
  batch_size_g: number | null;
  ambient_temp?: number | null;
  session_id?: string | null;
  temp_unit?: "C" | "F" | null;
  imported_at?: string | null;
  detector_version?: number | null;
  schema_version?: number | null;
  [k: string]: unknown;
}

// Every list tool filters the whole index, then pages (issue #46).
export interface RoastPageData {
  rows: RoastSummary[];
  count: number;
  total: number;
  truncated: boolean;
  next_cursor: string | null;
}

export interface ListRoastsResponse {
  summary: string;
  roasts: RoastSummary[];
  count: number;
  total: number;
  truncated: boolean;
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// Roast record — `get_roast` returns `Roast.model_dump()` with `points`
// emptied unless `include_points` is set.
// ---------------------------------------------------------------------------

export type TempSource = "bt" | "ibts" | "et";

export interface RoastDetail {
  roast_id: string;
  name: string;
  source_file: string;
  source_hash: string;
  source_type: string;
  roast_date: string | null;
  coffee_id: string | null;
  coffee_label: string | null;
  batch_size_g: number | null;
  roaster_model: string | null;
  operator: string | null;
  ambient_temp: number | null;
  humidity_pct: number | null;
  pressure_hpa: number | null;
  weight_green_g: number | null;
  weight_roasted_g: number | null;
  recipe_id: string | null;
  tags: string[];
  notes: string;
  session_id: string | null;
  roast_number_in_session: number | null;
  roast_degree: number | null;
  temp_unit: "C" | "F";
  source_temp_unit: "C" | "F" | null;
  primary_temp_source: TempSource;
  points: unknown[];
  points_omitted?: number;
  milestones: MilestoneRecord[];
  metrics: DerivedMetrics;
  rating: Rating;
  recipe: Recipe | null;
  planned_recipe: Recipe | null;
  imported_at: string | null;
  schema_version: number;
  detector_version: number;
  parse_warnings: string[];
  [k: string]: unknown;
}

export type MilestoneType =
  | "charge"
  | "turning_point"
  | "yellow"
  | "first_crack"
  | "drop";

// Where a *recorded* marker came from (issue #8).
export type RecordedSource = "manual" | "recipe_auto" | "unknown";

// What `Milestone.effective_source()` returns — which piece of evidence
// the effective value is.
export type EffectiveSource =
  | "corrected"
  | "annotated"
  | "manual"
  | "estimated"
  | "recipe_auto"
  | "unknown"
  | "none";

// `Milestone.model_dump()` — what `get_roast` carries in `milestones`.
// Note: no `effective_*` fields here; those are methods on the backend
// model and only appear on `DetectedMilestone` (get_detected_milestones).
export interface MilestoneRecord {
  milestone_type: MilestoneType;
  recorded_time_s: number | null;
  recorded_temp: number | null;
  recorded_temp_source: TempSource | null;
  recorded_temp_ibts: number | null;
  recorded_source: RecordedSource;
  estimated_time_s: number | null;
  estimated_temp: number | null;
  estimated_temp_source: TempSource | null;
  estimated_uncertainty_s: number | null;
  confidence: number;
  estimation_method: string | null;
  reason_codes: string[];
  notes: string | null;
  corrected_time_s: number | null;
  corrected_temp: number | null;
  corrected_temp_source: TempSource | null;
  correction_reason: string | null;
  corrected_at: string | null;
  observed_time_s: number | null;
  observed_temp: number | null;
  observed_temp_source: TempSource | null;
  observed_note: string | null;
  [k: string]: unknown;
}

// `get_detected_milestones` — the model dump plus the derived view the
// backend computes (which value is in play and how much it is worth).
export interface DetectedMilestone extends MilestoneRecord {
  effective_time_s: number | null;
  effective_temp: number | null;
  effective_temp_source: TempSource | null;
  effective_source: EffectiveSource;
  effective_confidence: number;
  detector_confidence: number;
}

export interface DetectedMilestonesData {
  roast_id: string;
  milestones: DetectedMilestone[];
}

// `DerivedMetrics.model_dump()`.
export interface DerivedMetrics {
  charge_temp: number | null;
  charge_temp_ibts: number | null;
  bt_at_charge: number | null;
  preheat_target_c: number | null;
  preheat_error_c: number | null;
  tp_temp: number | null;
  tp_bt_c: number | null;
  tp_time_s: number | null;
  ror_probe: string;
  max_ror: number | null;
  ror_at_first_crack: number | null;
  end_ror: number | null;
  drying_duration_s: number | null;
  maillard_duration_s: number | null;
  development_duration_s: number | null;
  drying_pct: number | null;
  maillard_pct: number | null;
  development_pct: number | null;
  phase_boundary_uncertainty_s: number | null;
  total_roast_time_s: number | null;
  drop_temp: number | null;
  drop_ibts_c: number | null;
  drop_bt_c: number | null;
  drop_temp_primary_c: number | null;
  drop_temp_probe: string | null;
  fc_ibts_c: number | null;
  fc_bt_c: number | null;
  weight_loss_pct: number | null;
  flick_detected: boolean;
  crash_detected: boolean;
  stall_detected: boolean;
  defect_reasons: string[];
  defects: Record<string, { verdict?: string; [k: string]: unknown }> | null;
  [k: string]: unknown;
}

// `Rating.model_dump()`. Always present on a roast; `stars === 0` means
// unrated (`Rating.unrated()`).
export interface Rating {
  stars: number;
  positive_notes: string[];
  negative_notes: string[];
  // Constrained by roastmcp/vocabulary.py on write.
  body: "light" | "medium" | "full" | null;
  brew_method: string | null;
  roast_level: RoastLevel | null;
  tasted_at: string | null;
  notes: string;
  rated_at: string | null;
  rated_via: string | null;
  [k: string]: unknown;
}

// Recipe shape mirrors `roastmcp.models.Recipe` — a temperature-triggered
// plan, not a time-indexed one. Each step says "at IBTS ≥ threshold_c,
// change these controls and/or mark this event". Unit is always Celsius
// regardless of the CSV's recorded unit — Celsius is what RoastTime's
// recipe UI shows and what the operator thinks in.

export type RecipeEvent =
  | "yellowing"
  | "first_crack"
  | "second_crack"
  | "drop";

export type RecipeSource = "planned" | "derived_from_csv" | "calibrated" | string;

export interface ControlState {
  // Aillio Bullet knobs. `null` in a step means "leave this knob
  // unchanged"; on `charge_controls` it means "not captured".
  power: number | null;  // P0–P9
  fan: number | null;    // F1–F9
  drum: number | null;   // D1–D9
}

export interface RecipeStep {
  threshold_c: number;
  source: "ibts" | "bt";
  control: ControlState;
  event: RecipeEvent | null;
  note: string | null;
  [k: string]: unknown;
}

export interface Recipe {
  recipe_id: string;
  coffee_id: string | null;
  name: string | null;
  version?: string | null;
  preheat_c: number | null;
  charge_controls: ControlState;
  steps: RecipeStep[];
  source: RecipeSource;
  schema_version?: number;
  notes?: string;
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// Time series — columnar transpose of `get_roast_timeseries` points.
// ---------------------------------------------------------------------------

export interface Timeseries {
  roast_id: string;
  start_s: number;
  end_s: number;
  max_points: number;
  count: number;
  elapsed_time_s: number[];
  bean_temp: (number | null)[];
  ibts_temp: (number | null)[];
  environmental_temp: (number | null)[];
  ror_bt: (number | null)[];
  ror_ibts: (number | null)[];
  // Bean-probe RoR, falling back to IBTS RoR per sample. `ror_source`
  // says which one was used overall (the probe the majority came from).
  ror: (number | null)[];
  ror_source: "bt" | "ibts" | null;
  power_setting: (number | null)[];
  fan_setting: (number | null)[];
  drum_setting: (number | null)[];
}

// ---------------------------------------------------------------------------
// Evaluation — `evaluate_roast` returns `Evaluation.to_dict()`
// (roastmcp/summary.py). Read `evidence` before `headline`: it says whether
// the headline is a judgement or an admission (issue #20).
// ---------------------------------------------------------------------------

export interface MilestoneEvidence {
  source: EffectiveSource;
  confidence: number;
  time_s: number | null;
  uncertainty_s: number | null;
  trustworthy: boolean;
}

export interface PeerEvidence {
  count: number;
  rated: number;
  comparable: boolean;
  notes: string[];
}

export interface Evidence {
  milestones: Record<string, MilestoneEvidence>;
  // defect kind -> "detected" | "not_detected" | "not_evaluated"
  defects: Record<string, string>;
  peers: PeerEvidence;
  development_assessable: boolean;
  defects_evaluated: boolean;
  clean: boolean;
  limitations: string[];
}

export interface TargetDelta {
  name: string;
  label: string;
  planned: number | null;
  planned_max: number | null;
  planned_source: string | null;
  actual: number | null;
  unit: string;
  delta: number | null;
  within_target: boolean | null;
}

export interface StepAdherence {
  threshold_c: number;
  probe: string;
  planned_control: Record<string, unknown>;
  planned_min_time_s: number | null;
  fired_at_s: number | null;
  executed_at_s: number | null;
  executed_control: Record<string, unknown>;
  followed: boolean | null;
  note: string | null;
}

export interface EndCondition {
  fired: string | null;
  temp_threshold_c: number | null;
  temp_reached_c: number | null;
  time_threshold_s: number | null;
  time_elapsed_s: number | null;
  margin_note: string | null;
}

export interface PlanAdherence {
  has_plan: boolean;
  recipe_name: string | null;
  recipe_version: string | null;
  targets: TargetDelta[];
  steps: StepAdherence[];
  end_condition: EndCondition | null;
  headline: string | null;
  statements: string[];
  reason: string | null;
}

export interface DevelopmentIndicator {
  name: string;
  label: string;
  value: number | null;
  unit: string;
  band_low: number | null;
  band_high: number | null;
  band_origin: string;
  band_note: string | null;
  position: "below" | "within" | "above" | null;
  direction: "under" | "ok" | "over" | null;
  marker_dependent: boolean;
  confidence: number;
  unavailable_reason: string | null;
}

export interface DevelopmentAssessment {
  verdict: "under" | "ok" | "over" | "conflicting" | "unknown" | string;
  headline: string;
  indicators: DevelopmentIndicator[];
  agreeing: string[];
  conflicting: string[];
  reasons: string[];
  advice: string[];
  band_origins: string[];
}

export interface ContextEffect {
  factor: string;
  metric: string;
  unit: string;
  delta: number;
  n_group: number;
  n_baseline: number;
  text: string;
}

// `_context_clause` — absent conditions are omitted, never zero-filled.
export interface EvaluationContext {
  ambient_temp?: number;
  humidity_pct?: number;
  pressure_hpa?: number;
  session_id?: string;
  position_in_session?: number | null;
  session_size?: number;
  effects?: ContextEffect[];
  text?: string;
}

export interface Evaluation {
  roast_id: string;
  headline: string;
  text: string;
  milestones: Record<string, number | null>;
  milestone_sources: Record<string, string>;
  milestone_confidence: Record<string, number>;
  dtr_is_plan_relative: boolean;
  context: EvaluationContext;
  development: DevelopmentAssessment | null;
  plan: PlanAdherence | null;
  evidence: Evidence | null;
  metrics: Record<string, unknown>;
  temp_unit: string;
  deltas_vs_history: Record<string, number> | null;
  defect_flags: string[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Marker provenance — `find_marker_discrepancies`, `review_milestone_confidence`
// ---------------------------------------------------------------------------

export interface MarkerDiscrepancy {
  milestone_type: MilestoneType;
  recorded_time_s: number;
  estimated_time_s: number;
  delta_s: number;
  recorded_source: RecordedSource;
  effective_source: EffectiveSource;
  confidence: number;
  detector_confidence: number;
  reason_codes: string[];
}

export interface MarkerDiscrepanciesData {
  roast_id: string;
  discrepancies: MarkerDiscrepancy[];
}

export interface LowConfidenceMilestone {
  milestone_type: MilestoneType;
  confidence: number;
  effective_source: EffectiveSource;
  recorded_source: RecordedSource;
  detector_confidence: number;
  estimation_method: string | null;
  reason_codes: string[];
}

export interface MilestoneConfidenceData {
  roast_id: string;
  low_confidence: LowConfidenceMilestone[];
}

// ---------------------------------------------------------------------------
// Misc records
// ---------------------------------------------------------------------------

export interface CoffeeProfile {
  coffee_id: string;
  name: string;
  origin_country: string | null;
  region: string | null;
  farm: string | null;
  process: string | null;
  variety: string | null;
  density_g_l: number | null;
  moisture_pct: number | null;
  screen_size: string | null;
  tags: string[];
  [k: string]: unknown;
}

// One ingestion log entry, adapted from the backend's `entries`. Local
// SQLite rows carry `at`/`message`; the cloud backend carries
// `timestamp`/`reason`. api.ts folds both into this shape.
export interface IngestionEvent {
  event_id?: string;
  source_file: string | null;
  source_hash: string | null;
  status: "ok" | "failed" | "skipped" | string;
  reason: string | null;
  roast_id: string | null;
  timestamp: string | null;
  detector_version: string | null;
  [k: string]: unknown;
}

// `_sync_health()` — `HealthSnapshot.to_dict()` plus three derived fields.
// `null` on the cloud backend, or whenever the uploader's state DB is not
// readable from this process.
export interface SyncHealth {
  last_success_at: string | null;
  last_failure_at: string | null;
  failing_since: string | null;
  last_error: string | null;
  last_error_category: string | null;
  hours_since_success: number | null;
  failing: boolean;
  stale: boolean;
}

export interface IngestionStatus {
  summary: string;
  recent_events: IngestionEvent[];
  counts: Record<string, number>;
  last_ingested_at: string | null;
  // Records produced by an older detector (issue #7). `null` when the
  // count could not be taken — different from 0.
  stale_detector_records: number | null;
  current_detector_version: number;
  sync: SyncHealth | null;
}

// Every handler that returns "prose + structured data" follows this envelope.
// `text` is a short template-rendered line we display verbatim above the rich
// view; `data` holds the fields the UI actually renders.
export interface Envelope<T> {
  text: string;
  data: T;
}

// ---- Improvement suggestions (roastmcp/suggestions.py) ------------------

export interface TargetBand {
  low: number;
  high: number;
  n: number;
  stars: number[];
  roast_ids: string[];
  method: "range" | "iqr" | string;
}

// What an improvement rests on. Empty (`{}`) for drift, which by
// construction has no rated evidence behind it.
export interface SuggestionEvidence {
  n_rated?: number;
  stars?: number[];
  band?: TargetBand;
}

export interface Suggestion {
  parameter: string;
  // "improvement" is advice backed by rated roasts; "drift" is a
  // description of how this batch differs from the typical one.
  kind: "improvement" | "drift" | string;
  current: number | null;
  target: number | null;
  delta: number | null;                // current - target
  direction: string;                   // "shorter" / "longer" / "cooler" / …
  rationale: string;
  cited_roast_ids: string[];
  target_source: "rated_band" | "peer_median_weighted" | string;
  temp_source: TempSource | null;
  evidence: SuggestionEvidence;
  caveats: string[];
}

export interface ReferenceRoastInfo {
  roast_id: string;
  stars: number;
  roast_date: string | null;
  rationale: string;
  source: "rating" | "consistency" | string;
}

export interface OperationalChange {
  phase: string;
  control: "power" | "fan" | "drum" | string;
  current: number | null;
  target: number | null;
  delta: number;
  direction: "increase" | "decrease" | string;
  description: string;
  cited_threshold_c: number | null;
  cited_roast_id: string;
}

export interface PhaseBands {
  yellowing_c: number;
  first_crack_c: number;
  source: "milestones" | "fixed" | "mixed" | string;
}

export interface RecipeAlignment {
  quality: number | null;
  phases: PhaseBands | null;
  // Why the per-step knob changes were suppressed, when they were.
  withheld: string | null;
}

export interface ImprovementSuggestionsData {
  roast_id: string;
  peer_count: number;
  only_rated: boolean;
  min_stars: number;
  use_reference: boolean;
  brew_method: string | null;
  reference: ReferenceRoastInfo | null;
  improvements: Suggestion[];
  drift: Suggestion[];
  rated_count: number;
  reason: string | null;
  ratings_needed: number;
  operational_changes: OperationalChange[];
  recipe_alignment: RecipeAlignment;
}

// ---- Reference roast + consistency -------------------------------------

export interface ReferenceRoastResponseData {
  coffee_label: string;
  reference: ReferenceRoastInfo | null;
}

export interface MetricStats {
  name: string;
  mean: number;
  stddev: number;
  cv: number;
  min: number;
  max: number;
  n: number;
}

export interface ConsistencyOutlier {
  roast_id: string;
  metric: string;
  value: number;
  mean: number;
  delta_stddevs: number;
}

export interface ConsistencyReport {
  coffee_label: string;
  n_roasts: number;
  score: number;
  grade: "excellent" | "good" | "variable" | "inconsistent" | string;
  per_metric: MetricStats[];
  outliers: ConsistencyOutlier[];
  rationale: string;
}

export interface ConsistencyReportResponseData {
  coffee_label: string;
  report: ConsistencyReport | null;
}

export interface CorrectionInput {
  milestone_type: MilestoneType;
  corrected_time_s?: number | null;
  corrected_temp?: number | null;
  reason?: string | null;
}

export interface RatingInput {
  stars: number;
  positive_notes: string[];
  negative_notes: string[];
  body: "light" | "medium" | "full" | null;
  brew_method: string | null;
  notes: string;
}

// Controlled vocabularies — duplicated from roastmcp/vocabulary.py so the
// form pickers don't have to make a round trip. Keep in sync.
export const POSITIVE_NOTES = [
  "sweet",
  "fruity",
  "floral",
  "chocolate",
  "nutty",
  "caramel",
  "citrus",
  "clean",
] as const;
export const NEGATIVE_NOTES = [
  "bitter",
  "papery",
  "roasty",
  "baked",
  "underdeveloped",
  "ashy",
  "astringent",
  "thin",
] as const;
export const BODY_VALUES = ["light", "medium", "full"] as const;
export const MILESTONE_TYPES: MilestoneType[] = [
  "charge",
  "turning_point",
  "yellow",
  "first_crack",
  "drop",
];

// ---------------------------------------------------------------------------
// AI evaluation
// ---------------------------------------------------------------------------

export interface AIEvaluation {
  roast_id: string;
  model: string;
  narrative: string;
  tokens_used: number | null;
  grounding: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Recipe trend
// ---------------------------------------------------------------------------

export type RoastLevel = "light" | "medium" | "full_city" | "dark";
export const ROAST_LEVELS: RoastLevel[] = [
  "light",
  "medium",
  "full_city",
  "dark",
];

export type TrendVerdict = "improving" | "steady" | "declining" | "too_few";

export interface TrendPoint {
  roast_id: string;
  roast_date: string | null;
  stars: number;
  inferred_roast_level: RoastLevel | null;
}

export interface RecipeTrendData {
  coffee_id: string;
  roast_level: RoastLevel | null;
  batch_size_band: [number, number] | null;
  n_total: number;
  n_rated: number;
  verdict: TrendVerdict;
  recent_mean: number | null;
  prior_mean: number | null;
  points: TrendPoint[];
  message: string;
}

export interface RecipeTrend extends RecipeTrendData {
  summary: string; // envelope's `text`
}

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

export type MaintenanceState = "ok" | "due" | "overdue" | "unknown";

export interface MaintenanceEvent {
  event_id: string;
  maintenance_type: string;
  performed_at: string;
  notes: string;
  roasts_since_previous: number | null;
  grams_since_previous: number | null;
}

export interface MaintenanceTypeStatus {
  maintenance_type: string;
  state: MaintenanceState;
  last_performed_at: string | null;
  roasts_since: number;
  grams_since: number;
  days_since: number | null;
  threshold_roasts: number | null;
  threshold_kg: number | null;
  threshold_days: number | null;
  pct_of_threshold: number;
  message: string;
  extra?: Record<string, unknown>;
}

export interface MaintenanceStatusData {
  overall: MaintenanceState;
  statuses: MaintenanceTypeStatus[];
}

export interface MaintenanceStatusResponse extends MaintenanceStatusData {
  summary: string;
}
